import {
  ADMIN_UWHC_PROVIDER_FIELDS,
  ADMIN_UWHC_SERVICE_FIELDS,
  AdminUwhcProviderField,
  AdminUwhcBreakdownItem,
  AdminUwhcCoverageStat,
  AdminUwhcLocationPin,
  AdminUwhcMonitoringStats,
  AdminUwhcProviderPayload,
  AdminUwhcProviderRow,
  AdminUwhcQualitySignal,
  getAdminUwhcProviderColumnLabel,
} from "../models/admin-uwhc.models";

type Coordinate = { lat: number; lng: number };

// Wikimedia BlankMap-World.svg uses Robinson projection coefficients.
const WORLD_MAP_VIEWBOX_X = -30;
const WORLD_MAP_VIEWBOX_Y = 61;
const WORLD_MAP_VIEWBOX_WIDTH = 2754;
const WORLD_MAP_VIEWBOX_HEIGHT = 1398;
const WORLD_MAP_CENTER_LONGITUDE = 11.25;
const ROBINSON_LATITUDE_COMPRESSION = 0.95623;

const ROBINSON_Y_BY_LATITUDE = [
  0, 0.062, 0.124, 0.186, 0.248, 0.31, 0.372, 0.434, 0.4958, 0.5571, 0.6176, 0.6769, 0.7346, 0.7903, 0.8435,
  0.8936, 0.9394, 0.9761, 1,
];

const ROBINSON_X_BY_LATITUDE = [
  1, 0.9986, 0.9954, 0.99, 0.9822, 0.973, 0.96, 0.9427, 0.9216, 0.8962, 0.8679, 0.835, 0.7986, 0.7597, 0.7186,
  0.6732, 0.6213, 0.5722, 0.5322,
];

const LOCATION_COORDINATES: Record<string, Coordinate> = {
  antwerp: { lat: 51.2194, lng: 4.4025 },
  australia: { lat: -25.2744, lng: 133.7751 },
  belgium: { lat: 50.5039, lng: 4.4699 },
  brazil: { lat: -14.235, lng: -51.9253 },
  busan: { lat: 35.1796, lng: 129.0756 },
  canada: { lat: 56.1304, lng: -106.3468 },
  chile: { lat: -35.6751, lng: -71.543 },
  china: { lat: 35.8617, lng: 104.1954 },
  durban: { lat: -29.8587, lng: 31.0218 },
  egypt: { lat: 26.8206, lng: 30.8025 },
  fremantle: { lat: -32.0569, lng: 115.7439 },
  germany: { lat: 51.1657, lng: 10.4515 },
  gibraltar: { lat: 36.1408, lng: -5.3536 },
  greece: { lat: 39.0742, lng: 21.8243 },
  hongkong: { lat: 22.3193, lng: 114.1694 },
  houston: { lat: 29.7604, lng: -95.3698 },
  india: { lat: 20.5937, lng: 78.9629 },
  indonesia: { lat: -0.7893, lng: 113.9213 },
  japan: { lat: 36.2048, lng: 138.2529 },
  malaysia: { lat: 4.2105, lng: 101.9758 },
  netherlands: { lat: 52.1326, lng: 5.2913 },
  newzealand: { lat: -40.9006, lng: 174.886 },
  panama: { lat: 8.538, lng: -80.7821 },
  philippines: { lat: 12.8797, lng: 121.774 },
  rotterdam: { lat: 51.9244, lng: 4.4777 },
  singapore: { lat: 1.3521, lng: 103.8198 },
  southafrica: { lat: -30.5595, lng: 22.9375 },
  southkorea: { lat: 35.9078, lng: 127.7669 },
  spain: { lat: 40.4637, lng: -3.7492 },
  turkey: { lat: 38.9637, lng: 35.2433 },
  uae: { lat: 23.4241, lng: 53.8478 },
  unitedarabemirates: { lat: 23.4241, lng: 53.8478 },
  unitedkingdom: { lat: 55.3781, lng: -3.436 },
  unitedstates: { lat: 37.0902, lng: -95.7129 },
  usa: { lat: 37.0902, lng: -95.7129 },
  vietnam: { lat: 14.0583, lng: 108.2772 },
};

export const toAdminUwhcGridRow = (row: AdminUwhcProviderRow, currentGridRowId?: string): AdminUwhcProviderRow => ({
  ...row,
  gridRowId: row.PROVIDER_UID
    ? `uwhc-provider-${row.PROVIDER_UID}`
    : currentGridRowId ?? row.gridRowId ?? `uwhc-row-${Date.now()}`,
  isDraft: false,
});

export const createDraftAdminUwhcRow = (): AdminUwhcProviderRow => ({
  gridRowId: `uwhc-draft-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  ID: null,
  isDraft: true,
  ...createEmptyAdminUwhcProviderPayload(),
});

export const createEmptyAdminUwhcProviderPayload = (): AdminUwhcProviderPayload =>
  ADMIN_UWHC_PROVIDER_FIELDS.reduce<AdminUwhcProviderPayload>((provider, field) => {
    provider[field] = null;
    return provider;
  }, {});

export const toAdminUwhcProviderPayload = (row: AdminUwhcProviderRow): AdminUwhcProviderPayload =>
  ADMIN_UWHC_PROVIDER_FIELDS.reduce<AdminUwhcProviderPayload>((provider, field) => {
    provider[field] = normalizeProviderValue(row[field]);
    return provider;
  }, {});

export const getProviderDisplayName = (row: AdminUwhcProviderRow | null) => {
  if (!row) {
    return "";
  }

  const company = normalizeProviderValue(row.COMPANY);
  const place = normalizeProviderValue(row.PLACE);

  return [company, place].filter(Boolean).join(" - ") || `Provider ${row.ID ?? "draft"}`;
};

export const normalizeProviderValue = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text || null;
};

export const getAdminUwhcProviderFieldOptions = (
  rows: AdminUwhcProviderRow[],
  field: AdminUwhcProviderField,
  currentValue: string | null | undefined,
): string[] => {
  const options = new Set<string>();
  const current = normalizeProviderValue(currentValue);

  if (current) {
    options.add(current);
  }

  rows.forEach((row) => {
    const value = normalizeProviderValue(row[field]);
    if (value) {
      options.add(value);
    }
  });

  if (ADMIN_UWHC_SERVICE_FIELDS.includes(field)) {
    options.add("Yes");
    options.add("No");
  }

  return Array.from(options).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
};

// === MONITORING ===

export const buildAdminUwhcMonitoringStats = (rows: AdminUwhcProviderRow[]): AdminUwhcMonitoringStats => {
  const total = rows.length;
  const preferred = countWhere(rows, (row) => isAffirmative(row.PREFERRED));
  const screened = countWhere(rows, (row) => isAffirmative(row.SCREENED));
  const service24_7 = countWhere(rows, (row) => isAffirmative(row.SERVICE_24_7));
  const withContact = countWhere(rows, hasContact);
  const countries = countUnique(rows.map((row) => row.COUNTRY));
  const ports = countUnique(rows.map((row) => row.PORT_NAME || row.CITY || row.PLACE));
  const locationPins = buildLocationPins(rows);
  const unmatchedLocations = countWhere(rows, (row) => !resolveLocationCoordinate(row));

  return {
    metrics: [
      {
        key: "providers",
        label: "Active Providers",
        value: total.toLocaleString(),
        caption: `${countries.toLocaleString()} countries / ${ports.toLocaleString()} locations`,
        tone: "accent",
      },
      {
        key: "preferred",
        label: "Preferred",
        value: preferred.toLocaleString(),
        caption: formatRate(preferred, total),
        tone: preferred > 0 ? "success" : "neutral",
      },
      {
        key: "screened",
        label: "Screened",
        value: screened.toLocaleString(),
        caption: formatRate(screened, total),
        tone: screened === total && total > 0 ? "success" : "warning",
      },
      {
        key: "service24_7",
        label: "24/7 Service",
        value: service24_7.toLocaleString(),
        caption: formatRate(service24_7, total),
        tone: service24_7 > 0 ? "success" : "neutral",
      },
      {
        key: "contactable",
        label: "Contactable",
        value: withContact.toLocaleString(),
        caption: formatRate(withContact, total),
        tone: withContact === total && total > 0 ? "success" : "warning",
      },
    ],
    topCountries: topCounts(
      rows.map((row) => row.COUNTRY),
      8,
    ),
    topRegions: topCounts(
      rows.map((row) => row.REGION),
      8,
    ),
    topServices: buildServiceCoverage(rows).slice(0, 10),
    contactCoverage: buildContactCoverage(rows),
    qualitySignals: buildQualitySignals(rows),
    locationPins,
    unmatchedLocations,
    updatedAt: new Date().toISOString(),
  };
};

export const isAffirmativeProviderValue = isAffirmative;

function buildServiceCoverage(rows: AdminUwhcProviderRow[]): AdminUwhcCoverageStat[] {
  const total = rows.length;

  return ADMIN_UWHC_SERVICE_FIELDS.map((field) => {
    const count = countWhere(rows, (row) => isAffirmative(row[field]));

    return {
      key: field,
      label: getAdminUwhcProviderColumnLabel(field),
      count,
      rate: total > 0 ? count / total : 0,
    };
  }).sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
}

function buildContactCoverage(rows: AdminUwhcProviderRow[]): AdminUwhcCoverageStat[] {
  const total = rows.length;
  const items: Array<{ key: string; label: string; count: number }> = [
    { key: "email", label: "Email", count: countWhere(rows, (row) => Boolean(normalizeProviderValue(row.EMAIL))) },
    { key: "phone", label: "Phone", count: countWhere(rows, (row) => Boolean(normalizeProviderValue(row.PHONE))) },
    { key: "any", label: "Any Contact", count: countWhere(rows, hasContact) },
    {
      key: "notes",
      label: "Operational Notes",
      count: countWhere(rows, (row) => Boolean(normalizeProviderValue(row.ADDITIONAL_INFO))),
    },
    {
      key: "regulations",
      label: "Regulations",
      count: countWhere(rows, (row) => Boolean(normalizeProviderValue(row.REGULATIONS))),
    },
  ];

  return items.map((item) => ({ ...item, rate: total > 0 ? item.count / total : 0 }));
}

function buildQualitySignals(rows: AdminUwhcProviderRow[]): AdminUwhcQualitySignal[] {
  const preferredNotScreened = countWhere(rows, (row) => isAffirmative(row.PREFERRED) && !isAffirmative(row.SCREENED));
  const missingContact = countWhere(rows, (row) => !hasContact(row));
  const missingLocation = countWhere(rows, (row) => !getLocationLabel(row));
  const emptyServices = countWhere(rows, (row) =>
    ADMIN_UWHC_SERVICE_FIELDS.every((field) => !isAffirmative(row[field])),
  );

  return [
    {
      key: "preferredNotScreened",
      label: "Preferred but not screened",
      value: preferredNotScreened,
      tone: preferredNotScreened > 0 ? "danger" : "success",
    },
    {
      key: "missingContact",
      label: "Missing contact",
      value: missingContact,
      tone: missingContact > 0 ? "warning" : "success",
    },
    {
      key: "missingLocation",
      label: "Missing location",
      value: missingLocation,
      tone: missingLocation > 0 ? "warning" : "success",
    },
    {
      key: "emptyServices",
      label: "Providers with no services",
      value: emptyServices,
      tone: emptyServices > 0 ? "warning" : "success",
    },
  ];
}

function buildLocationPins(rows: AdminUwhcProviderRow[]): AdminUwhcLocationPin[] {
  const grouped = rows.reduce<Record<string, { row: AdminUwhcProviderRow; count: number }>>((map, row) => {
    const match = resolveLocationCoordinate(row);
    if (!match) {
      return map;
    }

    const label = getLocationLabel(row) || "Unmatched";
    const key = normalizeLocationKey(label) || `unknown-${row.gridRowId}`;
    map[key] = map[key] ?? { row, count: 0 };
    map[key].count += 1;
    return map;
  }, {});

  return Object.entries(grouped)
    .flatMap<AdminUwhcLocationPin>(([key, item]) => {
      const match = resolveLocationCoordinate(item.row);
      if (!match) {
        return [];
      }

      return [
        {
          key,
          label: getProviderDisplayName(item.row),
          locationLabel: getLocationLabel(item.row) || "Unmatched location",
          x: toMapX(match.lng, match.lat),
          y: toMapY(match.lat),
          count: item.count,
          matchLevel: match.level,
        },
      ];
    })
    .sort((left, right) => right.count - left.count || left.locationLabel.localeCompare(right.locationLabel));
}

function resolveLocationCoordinate(row: AdminUwhcProviderRow): (Coordinate & { level: "place" | "country" }) | null {
  const placeCandidates = [row.PORT_NAME, row.CITY, row.PLACE].map(normalizeLocationKey).filter(Boolean);

  for (const candidate of placeCandidates) {
    const coordinate = LOCATION_COORDINATES[candidate];
    if (coordinate) {
      return { ...coordinate, level: "place" };
    }
  }

  const country = normalizeLocationKey(row.COUNTRY);
  if (country && LOCATION_COORDINATES[country]) {
    return { ...LOCATION_COORDINATES[country], level: "country" };
  }

  return null;
}

function getLocationLabel(row: AdminUwhcProviderRow): string {
  return [row.PORT_NAME, row.CITY, row.PLACE, row.COUNTRY].map(normalizeProviderValue).filter(Boolean).join(", ");
}

function normalizeLocationKey(value: unknown): string {
  return (
    normalizeProviderValue(value)
      ?.toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]/g, "") ?? ""
  );
}

function toMapX(lng: number, lat = 0): number {
  const projectedX =
    interpolateRobinsonCoefficient(ROBINSON_X_BY_LATITUDE, lat) *
    normalizeLongitude(lng - WORLD_MAP_CENTER_LONGITUDE);
  return WORLD_MAP_VIEWBOX_X + ((projectedX + 180) / 360) * WORLD_MAP_VIEWBOX_WIDTH;
}

function toMapY(lat: number): number {
  const projectedY =
    interpolateRobinsonCoefficient(ROBINSON_Y_BY_LATITUDE, lat) *
    ROBINSON_LATITUDE_COMPRESSION *
    Math.sign(lat);
  return WORLD_MAP_VIEWBOX_Y + ((1 - projectedY) / 2) * WORLD_MAP_VIEWBOX_HEIGHT;
}

function interpolateRobinsonCoefficient(table: number[], lat: number): number {
  const absoluteLatitude = Math.min(90, Math.abs(lat));
  const lowerIndex = Math.floor(absoluteLatitude / 5);
  const upperIndex = Math.min(table.length - 1, lowerIndex + 1);
  const ratio = (absoluteLatitude - lowerIndex * 5) / 5;

  return table[lowerIndex] + (table[upperIndex] - table[lowerIndex]) * ratio;
}

function normalizeLongitude(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

function isAffirmative(value: unknown): boolean {
  const normalized = normalizeProviderValue(value)?.toLowerCase() ?? "";
  return /^(yes|y|true|available|preferred|screened|24\/?7)(\b|[^a-z])/.test(normalized);
}

function hasContact(row: AdminUwhcProviderRow): boolean {
  return Boolean(normalizeProviderValue(row.EMAIL) || normalizeProviderValue(row.PHONE));
}

function countWhere(rows: AdminUwhcProviderRow[], predicate: (row: AdminUwhcProviderRow) => boolean): number {
  return rows.filter(predicate).length;
}

function countUnique(values: unknown[]): number {
  return new Set(values.map(normalizeProviderValue).filter(Boolean)).size;
}

function topCounts(values: unknown[], limit: number): AdminUwhcBreakdownItem[] {
  const counts = values.reduce<Record<string, number>>((map, value) => {
    const label = normalizeProviderValue(value);
    if (!label) {
      return map;
    }

    map[label] = (map[label] ?? 0) + 1;
    return map;
  }, {});

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
    .slice(0, limit);
}

function formatRate(value: number, total: number): string {
  if (total === 0) {
    return "0% of rows";
  }

  return `${Math.round((value / total) * 100)}% of rows`;
}
